import { MediaUploadField } from '@/src/modules/components/MediaUploadField';
import type { InvitationCustomData } from '@/src/types';
import { PLANE_SLOT_IDS, type PlaneSlotId } from '@/src/modules/templates/WeddingPlotPlane3D/planeSources';

export const PLOT_PLANE_TEMPLATE_ID = 'exp-wedding-plot-plane-3d';

export const PLOT_PLANE_PHOTO_SLOTS: ReadonlyArray<{ id: PlaneSlotId; label: string }> = [
  { id: 'sangeet', label: 'Sangeet' },
  { id: 'mehndi', label: 'Mehndi' },
  { id: 'hands', label: 'Mehndi hands / portal' },
  { id: 'ring', label: 'Ring ceremony' },
  { id: 'havan', label: 'Havan' },
  { id: 'pheras', label: 'Saat Pheras' },
];

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function readPlotPlanePhotos(
  customData: InvitationCustomData | null | undefined,
): Partial<Record<PlaneSlotId, string>> {
  const photos = asRecord(asRecord(customData?.plotPlane)?.photos);
  const out: Partial<Record<PlaneSlotId, string>> = {};
  if (!photos) return out;
  for (const id of PLANE_SLOT_IDS) {
    const value = photos[id];
    if (typeof value === 'string' && value.trim()) out[id] = value.trim();
  }
  return out;
}

/** Empty URL removes that key so the plane falls back to the sample plate. */
export function writePlotPlanePhoto(
  customData: InvitationCustomData | null | undefined,
  id: PlaneSlotId,
  url: string,
): InvitationCustomData {
  const photos = { ...readPlotPlanePhotos(customData) };
  const trimmed = url.trim();
  if (trimmed) photos[id] = trimmed;
  else delete photos[id];

  const plotPlane = asRecord(customData?.plotPlane) ?? {};
  const restPlot = { ...plotPlane };
  delete restPlot.photos;
  const next: InvitationCustomData = { ...(customData ?? {}) };

  if (Object.keys(photos).length === 0 && Object.keys(restPlot).length === 0) {
    delete next.plotPlane;
    return next;
  }

  next.plotPlane = Object.keys(photos).length > 0 ? { ...restPlot, photos } : restPlot;
  return next;
}

interface PlotPlanePhotoSlotsProps {
  customData: InvitationCustomData | null | undefined;
  /** Apply against the latest customData. The url is the only new value. */
  onSlotChange: (id: PlaneSlotId, url: string) => void;
  invitationId?: string | null;
  ensureInvitationId?: () => Promise<string | null>;
}

export function PlotPlanePhotoSlots({
  customData,
  onSlotChange,
  invitationId,
  ensureInvitationId,
}: PlotPlanePhotoSlotsProps) {
  const photos = readPlotPlanePhotos(customData);

  return (
    <div className="grid gap-5 md:grid-cols-2">
      <p className="md:col-span-2 text-sm text-on-surface-variant">
        One photo for each scene. An empty slot keeps the sample plate for that scene.
      </p>
      {PLOT_PLANE_PHOTO_SLOTS.map((slot) => (
        <MediaUploadField
          key={slot.id}
          id={`plot-plane-${slot.id}`}
          label={slot.label}
          accept="image"
          value={photos[slot.id] ?? ''}
          onChange={(url) => onSlotChange(slot.id, url)}
          invitationId={invitationId}
          ensureInvitationId={ensureInvitationId}
        />
      ))}
    </div>
  );
}
